import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  CurrencyFlagEmoji,
  DataTable,
  DateUI,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  PrimaryFullScreenSpinner,
  StatusUI,
  toast,
  UserProfile,
  uppercaseStatusToStatus,
} from "@repo/ui";
import { currencyCodeToName } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense, useCallback, useMemo, useState } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { RiArrowLeftFill } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { graphql } from "~/gql/client";
import type { AdminFxOnboardingRequestFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/fx-onboarding-requests")({
  head: () => opsHead("FX Onboarding Requests"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { data } = useSuspenseQuery(
    graphql(`
      query AdminFxOnboardingRequestsQueue {
        fxOnboardingRequestsQueue {
          id
          requests {
            ...AdminFxOnboardingRequest
          }
          pendingCount
        }
      }

      fragment AdminFxOnboardingRequest on FXOnboardingRequest {
        id
        organisation {
          id
          name
          squareLogoUrl
        }
        currency
        status
        userProfile {
          id
          ...UserProfileUI
        }
        createdAt
      }
    `),
  );

  const requests = useMemo(() => {
    const items = data.fxOnboardingRequestsQueue?.requests;
    if (!items) {
      return [];
    }
    return [...items].sort((a, b) => {
      if (a.status === "PENDING" && b.status !== "PENDING") {
        return -1;
      }
      if (b.status === "PENDING" && a.status !== "PENDING") {
        return 1;
      }
      return (
        b.createdAt.getUTCMilliseconds() - a.createdAt.getUTCMilliseconds()
      );
    });
  }, [data.fxOnboardingRequestsQueue?.requests]);

  return (
    <AdminPageContainer testId="admin-fx-onboarding-requests">
      <div>
        <DataTable
          data={requests}
          columns={columns}
          pageSize={50}
          className="overflow-hidden border border-zinc-200"
        />
      </div>
    </AdminPageContainer>
  );
}

const columns: ColumnDef<AdminFxOnboardingRequestFragment>[] = [
  {
    id: "organisation",
    header: "Organisation",
    cell: ({ row }) => (
      <Link to={`/organisations/${row.original.organisation?.id}`}>
        <div className="flex items-center gap-2">
          <Icon
            size="8"
            imageUrl={row.original.organisation?.squareLogoUrl ?? undefined}
            fallbackIcon={<PiBuildingOffice />}
          />
          {row.original.organisation?.name}
        </div>
      </Link>
    ),
  },
  {
    id: "currency",
    header: "Currency",
    cell: ({ row }) => (
      <div className="col-span-2 flex items-center gap-2">
        <CurrencyFlagEmoji
          className="h-6 w-8 rounded-sm text-[24px] shadow-sm saturate-[0.9]"
          currency={row.original.currency}
        />
        <div className="flex flex-col text-sm">
          <span>{currencyCodeToName[row.original.currency]}</span>
          <span className="text-secondary text-xs">
            {row.original.currency}
          </span>
        </div>
      </div>
    ),
  },
  {
    id: "requestedAt",
    header: "Requested at",
    cell: ({ row }) => (
      <DateUI date={row.original.createdAt} variant="DATETIME" />
    ),
  },
  {
    id: "userProfile",
    header: "Requesting user",
    cell: ({ row }) =>
      row.original.userProfile && <UserProfile {...row.original.userProfile} />,
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <StatusUI status={uppercaseStatusToStatus[row.original.status]} />
    ),
  },
  {
    id: "cancel",
    header: "Cancel",
    cell: ({ row }) =>
      row.original.status === "PENDING" && <Cancel {...row.original} />,
  },
];

const Cancel = ({
  id,
  currency,
  organisation,
  userProfile,
  createdAt,
}: AdminFxOnboardingRequestFragment) => {
  const [open, setOpen] = useState(false);

  const [cancelMutation, { loading }] = useMutation(
    graphql(`
      mutation CancelFXOnboardingRequest($input: CancelFXOnboardingRequestsInput!) {
        cancelFXOnboardingRequests(input: $input) {
          fxOnboardingRequestsQueue {
            id
            pendingCount
            requests {
              ...AdminFxOnboardingRequest
            }
          }
        }
      }
    `),
  );

  const handleClick = useCallback(async () => {
    const defaultErrorMessage =
      "An unexpected error occurred. Please try again later.";
    await cancelMutation({
      variables: {
        input: {
          ids: [id],
        },
      },
      onCompleted: () => {
        toast({
          kind: "success",
          title: "FX onboarding request cancelled",
        });
        setOpen(false);
      },
      onError: (error) => {
        const errorMessage =
          error.graphQLErrors?.[0]?.message || defaultErrorMessage;
        toast({
          kind: "error",
          title: "Failed to cancel FX onboarding request",
          description: errorMessage,
        });
      },
    });
  }, [cancelMutation, id]);

  return (
    <div>
      <Dialog onOpenChange={setOpen} {...{ open }}>
        <DialogTrigger>
          <Button variant="destructive">Cancel</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Confirm FX onboarding request cancellation
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <span>Currency: {currency}</span>
            <span>Organisation: {organisation?.name}</span>
            <span>
              Requested at: <DateUI date={createdAt} variant="DATETIME" />
            </span>
            {userProfile && (
              <span className="flex items-center gap-2">
                Requested by: <UserProfile {...userProfile} />
              </span>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="outline"
              onClick={(e) => {
                setOpen(false);
                e.preventDefault();
              }}
            >
              <RiArrowLeftFill className="mr-2" /> Back
            </Button>

            <Button
              variant="destructive"
              disabled={loading}
              loading={loading}
              onClick={handleClick}
            >
              Confirm cancellation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
